<template>
  <div class="invoice-detail">
    <div class="container">
      <div class="mb-4">
        <router-link to="/invoices" class="btn btn-link text-secondary ps-0">
          <i class="bi bi-arrow-left"></i> Back to Invoices
        </router-link>
      </div>
      
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
      
      <div v-else-if="error" class="alert alert-danger">
        {{ error }}
      </div>
      
      <div v-else-if="!invoice" class="alert alert-info">
        Invoice not found.
      </div>
      
      <div v-else>
        <div class="card border-0 shadow-sm mb-4">
          <div class="card-body p-4">
            <div class="row mb-4">
              <div class="col-md-6">
                <h2 class="mb-1">Invoice #{{ invoice.invoiceNumber }}</h2>
                <p class="text-muted mb-0">
                  Issued: {{ formatDate(invoice.date) }}
                </p>
                <p class="text-muted mb-0">
                  Due: {{ formatDate(invoice.dueDate) }}
                </p>
              </div>
              <div class="col-md-6 text-md-end">
                <span 
                  class="badge"
                  :class="{
                    'bg-success': invoice.status === 'Paid',
                    'bg-warning': invoice.status === 'Pending',
                    'bg-danger': invoice.status === 'Overdue'
                  }"
                >
                  {{ invoice.status }}
                </span>
                <p class="mt-2">
                  <button class="btn btn-primary me-2" @click="downloadInvoice">
                    <i class="bi bi-download"></i> Download PDF
                  </button>
                  <button 
                    v-if="invoice.status !== 'Paid'"
                    class="btn btn-success" 
                    @click="payInvoice"
                  >
                    <i class="bi bi-credit-card"></i> Pay Now
                  </button>
                </p>
              </div>
            </div>
            
            <hr>
            
            <div class="row mb-4">
              <div class="col-md-6">
                <h5>Billed To</h5>
                <p class="mb-0">{{ invoice.customer.name }}</p>
                <p class="mb-0">Account #: {{ invoice.customer.accountNumber }}</p>
                <p class="mb-0">{{ invoice.customer.email }}</p>
                <p>{{ invoice.customer.phone }}</p>
                <!-- Show addressee information if available -->
                <div v-if="invoice.calvi" class="mt-3">
                  <h6>Organization</h6>
                  <p class="mb-0">{{ invoice.calvi.organizationName }}</p>
                  <p v-if="invoice.calvi.vatNumber" class="mb-0">
                    VAT Number: {{ invoice.calvi.vatNumber }}
                  </p>
                  <p v-if="invoice.calvi.billingAccountNumber" class="mb-0">
                    Billing Account: {{ invoice.calvi.billingAccountNumber }}
                  </p>
                </div>
              </div>
              <div class="col-md-6">
                <h5>From</h5>
                <p class="mb-0">Streamline Telecom Inc.</p>
                <p class="mb-0">123 Telecom Avenue</p>
                <p class="mb-0">San Francisco, CA 94105</p>
                <p>support@streamline-telecom.com</p>

                <!-- Show Calvi-specific invoice details if available -->
                <div v-if="invoice.calvi" class="mt-3">
                  <h6>Invoice Details</h6>
                  <p v-if="invoice.calvi.paymentMethod" class="mb-0">
                    Payment Method: {{ formatPaymentMethod(invoice.calvi.paymentMethod) }}
                  </p>
                  <p v-if="invoice.calvi.collectionDate" class="mb-0">
                    Collection Date: {{ formatDate(invoice.calvi.collectionDate) }}
                  </p>
                </div>
              </div>
            </div>
            
            <!-- Cost Type Summary (only show if we have line items) -->
            <div v-if="invoice.calvi?.lineItems && invoice.calvi.lineItems.length > 0" class="mb-4">
              <h5>Charges Summary</h5>
              <div class="row">
                <div v-for="(total, costType) in summarizeCosts(invoice.calvi.lineItems)" 
                     :key="costType" 
                     class="col-md-3 col-sm-6 mb-3">
                  <div class="card border-0 shadow-sm h-100">
                    <div class="card-body">
                      <div class="d-flex justify-content-between align-items-center">
                        <h6 class="mb-0">{{ formatCostType(costType) }}</h6>
                        <small class="text-muted">
                          {{ getCostTypeItems(invoice.calvi.lineItems, costType).length }} items
                        </small>
                      </div>
                      <div class="text-end mt-2">
                        <strong>{{ formatCurrency(total, invoice.calvi?.currencyCode || 'EUR') }}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="table-responsive">
              <table class="table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Date</th>
                    <th>Quantity</th>
                    <th>Unit Price</th>
                    <th class="text-end">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <!-- If we have Calvi line items -->
                  <template v-if="invoice.calvi?.lineItems && invoice.calvi.lineItems.length > 0">
                    <tr v-for="(item, index) in invoice.calvi.lineItems" 
                        :key="`calvi-item-${index}`">
                      <td>
                        <div>
                          {{ item.description || 'Service Item' }}
                          <small class="text-secondary ms-2">
                            ({{ formatCostType(item.costType) }})
                          </small>
                        </div>
                        <small v-if="item.ciDisplayName && item.ciIdentification" class="text-muted">
                          {{ item.ciDisplayName }} ({{ item.ciIdentification }})
                        </small>
                      </td>
                      <td>
                        <small v-if="item.startDate && item.endDate">
                          {{ formatDate(item.startDate) }} - {{ formatDate(item.endDate) }}
                        </small>
                      </td>
                      <td>
                        <span v-if="item.costType === 'UC'">{{ formatDataUnits(item.quantity, true) }}</span>
                        <span v-else>{{ item.quantity || 1 }}</span>
                      </td>
                      <td>{{ formatCurrency(item.unitPrice || 0, invoice.calvi?.currencyCode || 'EUR') }}</td>
                      <td class="text-end">{{ formatCurrency(item.liAmount || 0, invoice.calvi?.currencyCode || 'EUR') }}</td>
                    </tr>
                  </template>
                  
                  <!-- Legacy items array -->
                  <template v-else-if="invoice.items">
                    <tr v-for="(item, index) in invoice.items" 
                        :key="`legacy-item-${index}`">
                      <td>{{ item.description }}</td>
                      <td>{{ formatDate(invoice.periodStart) }} - {{ formatDate(invoice.periodEnd) }}</td>
                      <td>1</td>
                      <td>{{ formatCurrency(item.amount, invoice.calvi?.currencyCode || 'EUR') }}</td>
                      <td class="text-end">{{ formatCurrency(item.amount, invoice.calvi?.currencyCode || 'EUR') }}</td>
                    </tr>
                  </template>
                  
                  <!-- Fallback if no items are available -->
                  <tr v-else>
                    <td>{{ invoice.calvi?.organizationName || 'Monthly Service' }}</td>
                    <td>{{ formatDate(invoice.periodStart) }} - {{ formatDate(invoice.periodEnd) }}</td>
                    <td>1</td>
                    <td>{{ formatCurrency(invoice.subtotal, invoice.calvi?.currencyCode || 'EUR') }}</td>
                    <td class="text-end">{{ formatCurrency(invoice.subtotal, invoice.calvi?.currencyCode || 'EUR') }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <td colspan="4" class="text-end">Subtotal</td>
                    <td class="text-end">{{ formatCurrency(invoice.subtotal, invoice.calvi?.currencyCode || 'EUR') }}</td>
                  </tr>
                  <tr v-if="invoice.calvi?.vatRate">
                    <td colspan="4" class="text-end">VAT ({{ invoice.calvi.vatRate }}%)</td>
                    <td class="text-end">{{ formatCurrency(invoice.taxAmount, invoice.calvi?.currencyCode || 'EUR') }}</td>
                  </tr>
                  <tr v-else>
                    <td colspan="4" class="text-end">VAT</td>
                    <td class="text-end">{{ formatCurrency(invoice.taxAmount, invoice.calvi?.currencyCode || 'EUR') }}</td>
                  </tr>
                  <tr>
                    <td colspan="4" class="text-end fw-bold">Total</td>
                    <td class="text-end fw-bold">{{ formatCurrency(invoice.amount, invoice.calvi?.currencyCode || 'EUR') }}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            
            <div class="mt-4">
              <h5>Payment Methods</h5>
              <p>Pay securely online with a credit or debit card.</p>
            </div>
            
            <div v-if="invoice.status === 'Paid'" class="alert alert-success">
              <h5>
                <i class="bi bi-check-circle-fill"></i> Payment Received
              </h5>
              <p class="mb-0">
                Thank you for your payment of {{ formatCurrency(invoice.amount) }} on {{ formatDate(invoice.paidDate) }}.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'

export default {
  name: 'InvoiceDetailView',
  setup() {
    const store = useStore()
    const router = useRouter()
    const route = useRoute()
    
    // State
    const loading = ref(true)
    const error = ref(null)
    const invoiceId = computed(() => route.params.id)
    
    // Computed properties
    const invoice = computed(() => store.getters['invoices/currentInvoice'])
    
    // Methods
    const formatDate = (dateString) => {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString()
    }
    
    const formatCurrency = (amount, currency = 'EUR') => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency
      }).format(amount)
    }
    
    const formatDataUnits = (bytes, showUnitsOnly = false) => {
      if (!bytes || bytes === 0) return '0';
      
      const units = ['B', 'KB', 'MB', 'GB', 'TB'];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      const formattedValue = (bytes / Math.pow(1024, i)).toFixed(2);
      
      return showUnitsOnly ? `${formattedValue} ${units[i]}` : `(${formattedValue} ${units[i]})`;
    }
    
    const formatCostType = (code) => {
      // Cost-type map
      const costTypes = {
        'UC': 'Usage',
        'RC': 'Recurring',
        'ON': 'One-off',
        'CR': 'Credit',
        'DC': 'Discount',
        'EP': 'External Payment',
        'OT': 'Other'
      };
      return costTypes[code] || code;
    }
    
    const getCostTypeBadgeClass = (code) => {
      // Map cost types to Bootstrap badge classes
      const badgeClasses = {
        'UC': 'bg-info',
        'RC': 'bg-secondary',
        'ON': 'bg-warning',
        'CR': 'bg-success',
        'DC': 'bg-danger',
        'EP': 'bg-success',
        'OT': 'bg-light text-dark'
      };
      return badgeClasses[code] || 'bg-secondary';
    }
    
    const getCostTypeCardClass = (code) => {
      // Map cost types to card border classes
      const cardClasses = {
        'UC': 'border-info',
        'RC': 'border-secondary',
        'ON': 'border-warning',
        'CR': 'border-success',
        'DC': 'border-danger',
        'EP': 'border-success',
        'OT': 'border-light'
      };
      return cardClasses[code] || '';
    }
    
    // Group items by cost type and calculate total for each group
    const summarizeCosts = (lineItems) => {
      if (!lineItems || !lineItems.length) return {};
      
      const summary = {};
      lineItems.forEach(item => {
        const costType = item.costType || 'OT';
        if (!summary[costType]) {
          summary[costType] = 0;
        }
        summary[costType] += item.liAmount || 0;
      });
      
      return summary;
    }
    
    // Get all items of a specific cost type
    const getCostTypeItems = (lineItems, costType) => {
      if (!lineItems || !lineItems.length) return [];
      return lineItems.filter(item => item.costType === costType);
    }
    
    const formatPaymentMethod = (code) => {
      // Payment method codes from Calvi API
      const paymentMethods = {
        '30': 'Credit Transfer',
        '59': 'Direct Debit',
        '97': 'Credit Card'
      }
      return paymentMethods[code] || code
    }
    
    const downloadInvoice = () => {
      store.dispatch('invoices/downloadInvoice', invoiceId.value)
    }
    
    const payInvoice = async () => {
      try {
        await store.dispatch('invoices/markAsPaid', invoiceId.value)
      } catch (err) {
        error.value = 'Failed to process payment. Please try again.'
        console.error(err)
      }
    }
    
    // Fetch invoice on component mount
    onMounted(async () => {
      try {
        loading.value = true
        await store.dispatch('invoices/fetchInvoice', invoiceId.value)
      } catch (err) {
        error.value = 'Failed to load invoice. Please try again.'
        console.error(err)
      } finally {
        loading.value = false
      }
    })
    
    return {
      loading,
      error,
      invoice,
      invoiceId,
      formatDate,
      formatCurrency,
      formatDataUnits,
      formatCostType,
      getCostTypeBadgeClass,
      getCostTypeCardClass,
      summarizeCosts,
      getCostTypeItems,
      formatPaymentMethod,
      downloadInvoice,
      payInvoice
    }
  }
}
</script>

<style scoped>
.invoice-detail {
  padding: 20px 0;
}
</style>